/**
 * Pure helpers for the "Export war plan" feature.
 *
 * - Computes a bounding box around the currently active hexagons (i.e. hexagons
 *   that are NOT in `disabledRegionKeys`).
 * - Filters the user-placed map objects to keep only those relevant for the
 *   export image: combat targets and logistics hubs, and only when they sit
 *   in a non-disabled region.
 */

import type { MapArrow, MapObject } from '@warplan/shared';
import {
  ATLAS,
  TILE_W,
  TILE_H,
  REGION_LABELS,
  findRegionKey,
  findRegionLabel,
} from './foxhole-maps.js';
import { buildFacilityItems, buildMsupItems } from './fac-build-msup-export.js';

/** Icon IDs considered "logistics hubs" for the export legend. */
export const EXPORT_HUB_IDS: ReadonlySet<string> = new Set([
  'LOGISTIC_HUB',
  'LOGISTIC_SEAPORT',
  'LOGISTIC_STORAGEDEPOT',
  'LOGISTIC_AIRWAY',
]);

/** World-pixel padding added around the active hex bounding box. */
export const EXPORT_PADDING_PX = 80;

/** Width (CSS pixels) of the map column rendered in the export view. */
export const EXPORT_MAP_WIDTH_PX = 1200;

/** Width (CSS pixels) of the war-plan panel column rendered in the export. */
export const EXPORT_PANEL_WIDTH_PX = 320;

/** Maximum height (CSS pixels) for the export map area. */
export const EXPORT_MAP_MAX_HEIGHT_PX = 1400;

export interface ExportBoundingBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A single item surfaced in the export filter panel. */
export type ExportFilterItem =
  | ExportFilterObjectItem
  | ExportFilterArrowItem
  | ExportFilterFusionItem;

/** A map object (combat / hub / fac / build / msup) listed in the panel. */
export interface ExportFilterObjectItem {
  type: 'object';
  /** Shared id used in the `excludedIds` set. */
  id: string;
  obj: MapObject;
  /** ATLAS tile key for the hex this object sits in, or null if outside the atlas. */
  hexKey: string | null;
  /** Human-readable hex label used as the group header in the panel. */
  hexLabel: string;
  /** Nearest "minor" sub-region label (Foxhole WarAPI), if available. */
  minorLabel: string | null;
}

/** A map arrow listed in the panel (placed in the hex of its midpoint). */
export interface ExportFilterArrowItem {
  type: 'arrow';
  /** Shared id used in the `excludedIds` set. */
  id: string;
  arrow: MapArrow;
  hexKey: string | null;
  hexLabel: string;
  minorLabel: string | null;
}

/**
 * A "fusion" of multiple MapObjects displayed as a single row in the panel
 * — mirrors the merging done by the fac/build/msup export view (tunnel + MSup,
 * material + assmats). Toggling a fusion toggles every underlying object id.
 */
export interface ExportFilterFusionItem {
  type: 'fusion';
  /** Synthetic id used to key React lists; NOT placed in `excludedIds`. */
  id: string;
  /** All underlying MapObject ids — toggled as a group via `objectIds`. */
  objectIds: readonly string[];
  /** Primary marker (icon + position source). */
  primary: MapObject;
  /** Secondary markers merged into this row (e.g. MSup variant, assmats). */
  secondaries: readonly MapObject[];
  /** Kind of fusion — drives label/badge rendering. */
  fusionKind: 'msup' | 'facility';
  hexKey: string | null;
  hexLabel: string;
  minorLabel: string | null;
}

/** Merging strategy applied by `buildExportFilterItems`. */
export type ExportFilterMergeStrategy = 'none' | 'msup' | 'facility' | 'both';

/**
 * Returns the underlying MapObject ids that should participate in the
 * `excludedIds` set for the given item. Fusions span multiple ids; objects
 * and arrows expose only their own id.
 */
export function getItemObjectIds(item: ExportFilterItem): readonly string[] {
  return item.type === 'fusion' ? item.objectIds : [item.id];
}

/** WarAPI label entry consumed when computing the nearest minor sub-region. */
export interface ExportFilterWarLabel {
  wx: number;
  wy: number;
  text: string;
  kind: 'major' | 'minor';
}

/** A hexagon entry surfaced in the export filter panel "hexes" column. */
export interface AvailableHex {
  key: string;
  label: string;
}

const EMPTY_IDS: ReadonlySet<string> = new Set();
const EMPTY_KEYS: ReadonlySet<string> = new Set();

/**
 * Computes a world-space bounding box covering every hex tile whose key is NOT
 * in `disabled`. Returns null when every hex is disabled.
 */
export function computeActiveBoundingBox(
  disabled: ReadonlySet<string>,
): ExportBoundingBox | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  let count = 0;
  for (const tile of ATLAS) {
    if (disabled.has(tile.key)) continue;
    count += 1;
    const left = tile.x - TILE_W / 2;
    const right = tile.x + TILE_W / 2;
    const top = tile.y - TILE_H / 2;
    const bottom = tile.y + TILE_H / 2;
    if (left < minX) minX = left;
    if (top < minY) minY = top;
    if (right > maxX) maxX = right;
    if (bottom > maxY) maxY = bottom;
  }
  if (count === 0) return null;
  return {
    x: minX - EXPORT_PADDING_PX,
    y: minY - EXPORT_PADDING_PX,
    width: maxX - minX + EXPORT_PADDING_PX * 2,
    height: maxY - minY + EXPORT_PADDING_PX * 2,
  };
}

/** True if `obj` is a combat target or a logistics hub. */
export function isExportableMarker(obj: MapObject): boolean {
  if (obj.kind === 'combat') return true;
  return EXPORT_HUB_IDS.has(obj.metadata.iconId);
}

/**
 * True if `arrow` belongs to the war-plan export scope.
 * Mirrors `isExportableMarker`: combat + logistic categories only.
 */
export function isExportableArrow(arrow: MapArrow): boolean {
  return arrow.kind === 'combat' || arrow.kind === 'logistic';
}

/** Nearest WarAPI "minor" label to (wx, wy), or null if none provided. */
function findNearestMinorLabel(
  wx: number,
  wy: number,
  warLabels: ReadonlyArray<ExportFilterWarLabel>,
): string | null {
  let best: ExportFilterWarLabel | null = null;
  let bestDist = Infinity;
  for (const lbl of warLabels) {
    if (lbl.kind !== 'minor') continue;
    const dx = lbl.wx - wx;
    const dy = lbl.wy - wy;
    const d = dx * dx + dy * dy;
    if (d < bestDist) {
      bestDist = d;
      best = lbl;
    }
  }
  return best?.text ?? null;
}

/**
 * Returns the subset of `objects` that should appear on the export image:
 * combat + logistics hubs, excluding those located in disabled regions and any
 * object whose id is in `excludedIds` (manual user selection).
 */
export function filterMarkersForExport(
  objects: ReadonlyArray<MapObject>,
  disabled: ReadonlySet<string>,
  excludedIds: ReadonlySet<string> = EMPTY_IDS,
): MapObject[] {
  return objects.filter((o) => {
    if (!isExportableMarker(o)) return false;
    if (excludedIds.has(o.id)) return false;
    if (disabled.size === 0) return true;
    const key = findRegionKey(o.position.x, o.position.y);
    return key === null || !disabled.has(key);
  });
}

/**
 * Builds the ordered list of exportable items for the export filter panel.
 *
 * `prefiltered` must already contain only the markers relevant to the export
 * type (e.g. combat + hubs for war-plan, fac/build/msup for the other modal).
 *
 * Objects whose hex is in `disabled` are excluded. Arrows are placed in the
 * hex of their **starting vertex**; arrows whose start lies in a disabled hex
 * are excluded.
 *
 * `mergeStrategy` controls how merge-eligible objects are grouped into a
 * single "fusion" row (mirroring the export PNG and the left-side AssetList):
 * - `'none'`  : no merging (war-plan modal)
 * - `'msup'`  : merge maintenance tunnels with their nearest MSup variant
 * - `'facility'`: merge materials with nearby assmats
 * - `'both'`  : apply both msup + facility merging
 *
 * Results are sorted by hex label, then by display type (objects → fusions →
 * arrows), then by a stable secondary key.
 */
export function buildExportFilterItems(
  prefiltered: ReadonlyArray<MapObject>,
  disabled: ReadonlySet<string>,
  warLabels: ReadonlyArray<ExportFilterWarLabel> = [],
  arrows: ReadonlyArray<MapArrow> = [],
  mergeStrategy: ExportFilterMergeStrategy = 'none',
): ExportFilterItem[] {
  const items: ExportFilterItem[] = [];
  const consumedObjectIds = new Set<string>();

  // Fusions first — they "consume" the underlying objects so the same id never
  // appears twice in the list.
  if (mergeStrategy === 'msup' || mergeStrategy === 'both') {
    for (const m of buildMsupItems(prefiltered)) {
      if (m.kind !== 'merged') continue;
      consumedObjectIds.add(m.tunnel.id);
      consumedObjectIds.add(m.msup.id);
      const pos = m.tunnel.position;
      const hexKey = findRegionKey(pos.x, pos.y);
      if (hexKey !== null && disabled.has(hexKey)) continue;
      const hexLabel = hexKey !== null ? findRegionLabel(pos.x, pos.y) : '— Sans région';
      const minorLabel = findNearestMinorLabel(pos.x, pos.y, warLabels);
      items.push({
        type: 'fusion',
        id: `fusion-msup-${m.tunnel.id}`,
        objectIds: [m.tunnel.id, m.msup.id],
        primary: m.tunnel,
        secondaries: [m.msup],
        fusionKind: 'msup',
        hexKey,
        hexLabel,
        minorLabel,
      });
    }
  }
  if (mergeStrategy === 'facility' || mergeStrategy === 'both') {
    for (const f of buildFacilityItems(prefiltered)) {
      if (f.kind !== 'merged') continue;
      consumedObjectIds.add(f.material.id);
      for (const a of f.assmats) consumedObjectIds.add(a.id);
      const pos = f.material.position;
      const hexKey = findRegionKey(pos.x, pos.y);
      if (hexKey !== null && disabled.has(hexKey)) continue;
      const hexLabel = hexKey !== null ? findRegionLabel(pos.x, pos.y) : '— Sans région';
      const minorLabel = findNearestMinorLabel(pos.x, pos.y, warLabels);
      items.push({
        type: 'fusion',
        id: `fusion-facility-${f.material.id}`,
        objectIds: [f.material.id, ...f.assmats.map((a) => a.id)],
        primary: f.material,
        secondaries: f.assmats,
        fusionKind: 'facility',
        hexKey,
        hexLabel,
        minorLabel,
      });
    }
  }

  // Remaining standalone objects
  for (const obj of prefiltered) {
    if (consumedObjectIds.has(obj.id)) continue;
    const hexKey = findRegionKey(obj.position.x, obj.position.y);
    if (hexKey !== null && disabled.has(hexKey)) continue;
    const hexLabel =
      hexKey !== null ? findRegionLabel(obj.position.x, obj.position.y) : '— Sans région';
    const minorLabel = findNearestMinorLabel(obj.position.x, obj.position.y, warLabels);
    items.push({ type: 'object', id: obj.id, obj, hexKey, hexLabel, minorLabel });
  }

  for (const arrow of arrows) {
    const origin = arrow.points[0];
    if (!origin) continue;
    const hexKey = findRegionKey(origin.x, origin.y);
    if (hexKey !== null && disabled.has(hexKey)) continue;
    const hexLabel = hexKey !== null ? findRegionLabel(origin.x, origin.y) : '— Sans région';
    const minorLabel = findNearestMinorLabel(origin.x, origin.y, warLabels);
    items.push({ type: 'arrow', id: arrow.id, arrow, hexKey, hexLabel, minorLabel });
  }

  const typeOrder: Record<ExportFilterItem['type'], number> = {
    object: 0,
    fusion: 1,
    arrow: 2,
  };
  return items.sort((a, b) => {
    const cmp = a.hexLabel.localeCompare(b.hexLabel, undefined, { sensitivity: 'base' });
    if (cmp !== 0) return cmp;
    if (a.type !== b.type) return typeOrder[a.type] - typeOrder[b.type];
    if (a.type === 'object' && b.type === 'object') {
      return a.obj.metadata.iconId.localeCompare(b.obj.metadata.iconId);
    }
    return a.id.localeCompare(b.id);
  });
}


/**
 * Returns the region keys that should be treated as "effectively disabled" for
 * the export bounding box: the original admin-disabled set, plus any hex where
 * every exportable marker has been manually excluded by the user, plus any hex
 * the user has manually deselected in the "hexes" column.
 */
export function computeEffectiveDisabledRegions(
  prefiltered: ReadonlyArray<MapObject>,
  originalDisabled: ReadonlySet<string>,
  excludedIds: ReadonlySet<string>,
  excludedHexKeys: ReadonlySet<string> = EMPTY_KEYS,
): Set<string> {
  const result = new Set(originalDisabled);
  for (const k of excludedHexKeys) result.add(k);
  if (excludedIds.size === 0) return result;

  // Collect the marker ids per active hex
  const hexMarkerIds = new Map<string, string[]>();
  for (const obj of prefiltered) {
    const key = findRegionKey(obj.position.x, obj.position.y);
    if (key === null || originalDisabled.has(key)) continue;
    const ids = hexMarkerIds.get(key) ?? [];
    ids.push(obj.id);
    hexMarkerIds.set(key, ids);
  }

  // Disable any hex where every marker has been excluded
  for (const [hexKey, ids] of hexMarkerIds) {
    if (ids.every((id) => excludedIds.has(id))) {
      result.add(hexKey);
    }
  }

  return result;
}

/**
 * Returns the list of hexagons available in the export filter "hexes" column.
 * Excludes admin-disabled hexes. Sorted by label.
 */
export function getAvailableHexes(disabled: ReadonlySet<string>): AvailableHex[] {
  const items: AvailableHex[] = [];
  for (const tile of ATLAS) {
    if (disabled.has(tile.key)) continue;
    items.push({
      key: tile.key,
      label: REGION_LABELS[tile.key] ?? tile.mapName,
    });
  }
  return items.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
}

/** Builds a default file name for the downloaded PNG. */
export function buildExportFileName(now: Date = new Date()): string {
  const pad = (n: number): string => n.toString().padStart(2, '0');
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}` +
    `-${pad(now.getHours())}${pad(now.getMinutes())}`;
  return `warplan-${stamp}.png`;
}

/**
 * Converts a `data:` URL (as produced by `html-to-image`'s `toPng`) into a
 * `Blob`, without going through `fetch()`. Chrome treats `fetch()` on a
 * `data:` URL as a network request subject to the CSP `connect-src`
 * directive, so it gets blocked under a strict CSP even though no network
 * access actually happens. Decoding the base64 payload manually avoids that.
 */
export function dataUrlToBlob(dataUrl: string): Blob {
  const [header, base64] = dataUrl.split(',', 2);
  const mimeMatch = /^data:(.*?)(;base64)?$/.exec(header ?? '');
  const mime = mimeMatch?.[1] || 'application/octet-stream';
  const binary = atob(base64 ?? '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new Blob([bytes], { type: mime });
}

/**
 * Returns the subset of `arrows` to render on an export image: an arrow is
 * kept iff its **starting vertex** (`points[0]`) sits in a non-disabled
 * region. This mirrors how arrows are placed in the export filter panel
 * (under the hex of their first vertex) and matches the per-position logic
 * used for objects (`filterMarkersForExport`).
 *
 * Arrows whose id is in `excludedIds` (manual user selection) are always
 * dropped. When no region is disabled, all non-excluded arrows pass.
 */
export function filterArrowsForExport(
  arrows: ReadonlyArray<MapArrow>,
  disabled: ReadonlySet<string>,
  excludedIds: ReadonlySet<string> = EMPTY_IDS,
): MapArrow[] {
  return arrows.filter((a) => {
    if (excludedIds.has(a.id)) return false;
    if (disabled.size === 0) return true;
    const origin = a.points[0];
    if (!origin) return false;
    const key = findRegionKey(origin.x, origin.y);
    return key === null || !disabled.has(key);
  });
}
